import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useTasks } from '@/contexts/task/TaskContext';
import { useDeleteSuggestedTaskMutation } from './mutations/use-delete-suggested-task-mutation';
import { SuggestedTask } from './suggested-task-types';
import { toast } from 'sonner';

interface BulkTaskActionsProps {
  goalId: string;
}

/**
 * Hook for handling bulk task actions
 * Simplified approach - no auto-replacement, just add/remove operations
 */
export const useBulkTaskActions = ({ goalId }: BulkTaskActionsProps) => {
  const { user } = useUser();
  const { addTask: addTaskToContext } = useTasks();
  const deleteSuggestedTaskMutation = useDeleteSuggestedTaskMutation(goalId);
  const queryClient = useQueryClient();

  // Create actual task from suggestion and remove suggestion
  const createTaskFromSuggestion = useCallback(async (suggestion: SuggestedTask) => {
    if (!user) return;

    try {
      console.log('[BulkTaskActions] Creating actual task from suggestion:', suggestion.title);

      // Calculate due date 1 week from now
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
      const dueDateString = oneWeekFromNow.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      // Use Task Context to add task to Team Tasks
      addTaskToContext({
        title: suggestion.title,
        description: suggestion.description || '',
        goalId: goalId,
        priority: null, // Leave priority blank
        dueDate: dueDateString
        // cardOrder will be calculated by the mutation to place at top
      });

      // Remove the suggestion from the list
      await deleteSuggestedTaskMutation.mutateAsync(suggestion.id);

      console.log('[BulkTaskActions] Task created and suggestion removed successfully');

    } catch (error: any) {
      console.error('[BulkTaskActions] Failed to create task from suggestion:', error);
      throw error;
    }
  }, [user, goalId, addTaskToContext, deleteSuggestedTaskMutation]);

  // Bulk add to project mutation
  const bulkAddToProjectMutation = useMutation({
    mutationFn: async ({ selectedTaskIds, selectedTasks }: { 
      selectedTaskIds: string[], 
      selectedTasks: SuggestedTask[] 
    }) => {
      console.log('[BulkTaskActions] Adding multiple tasks to project:', selectedTasks.length);

      // Process all tasks in parallel
      const results = await Promise.allSettled(
        selectedTasks.map(task => createTaskFromSuggestion(task))
      );

      // Check for any failures
      const failures = results.filter(result => result.status === 'rejected');
      const successes = results.filter(result => result.status === 'fulfilled');

      if (failures.length > 0) {
        console.error('[BulkTaskActions] Some tasks failed to add:', failures);
        
        if (successes.length === 0) {
          throw new Error('Failed to add any tasks to project');
        } else {
          // Partial success
          console.warn(`[BulkTaskActions] ${failures.length} out of ${selectedTasks.length} tasks failed`);
        }
      }

      return {
        totalTasks: selectedTasks.length,
        successCount: successes.length,
        failureCount: failures.length
      };
    },
    onSuccess: ({ totalTasks, successCount, failureCount }) => {
      if (failureCount === 0) {
        toast.success(`Successfully added ${successCount} task${successCount > 1 ? 's' : ''} to Team Tasks!`);
      } else {
        toast.success(`Added ${successCount} out of ${totalTasks} tasks to Team Tasks`);
        if (failureCount > 0) {
          toast.error(`${failureCount} task${failureCount > 1 ? 's' : ''} failed to add`);
        }
      }

      // Invalidate task queries to refresh UI
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["suggestedTasks", user.id, goalId] });
      }
    },
    onError: (error: any) => {
      console.error('[BulkTaskActions] Bulk add to project failed:', error);
      toast.error('Failed to add tasks to project');
    }
  });

  // Bulk clear selection mutation (just removes tasks, no replacement)
  const bulkClearSelectionMutation = useMutation({
    mutationFn: async ({ selectedTaskIds }: { selectedTaskIds: string[] }) => {
      console.log('[BulkTaskActions] Clearing multiple task selections:', selectedTaskIds.length);

      // Remove all selected tasks in parallel
      const results = await Promise.allSettled(
        selectedTaskIds.map(taskId => deleteSuggestedTaskMutation.mutateAsync(taskId))
      );

      // Check for any failures
      const failures = results.filter(result => result.status === 'rejected');
      const successes = results.filter(result => result.status === 'fulfilled');

      if (failures.length > 0) {
        console.error('[BulkTaskActions] Some tasks failed to clear:', failures);
        
        if (successes.length === 0) {
          throw new Error('Failed to clear any tasks');
        }
      }

      return {
        totalTasks: selectedTaskIds.length,
        successCount: successes.length,
        failureCount: failures.length
      };
    },
    onSuccess: ({ totalTasks, successCount, failureCount }) => {
      if (failureCount === 0) {
        toast.success(`Cleared ${successCount} task${successCount > 1 ? 's' : ''} from recommendations`);
      } else {
        toast.success(`Cleared ${successCount} out of ${totalTasks} tasks`);
        if (failureCount > 0) {
          toast.error(`${failureCount} task${failureCount > 1 ? 's' : ''} failed to clear`);
        }
      }

      // Invalidate task queries to refresh UI
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["suggestedTasks", user.id, goalId] });
      }
    },
    onError: (error: any) => {
      console.error('[BulkTaskActions] Bulk clear selection failed:', error);
      toast.error('Failed to clear task selections');
    }
  });

  // Simplified bulk add to project function
  const addToProject = useCallback(async (selectedTaskIds: string[], selectedTasks: SuggestedTask[]) => {
    return bulkAddToProjectMutation.mutateAsync({ selectedTaskIds, selectedTasks });
  }, [bulkAddToProjectMutation]);

  // Simplified bulk clear selection function
  const clearSelection = useCallback(async (selectedTaskIds: string[]) => {
    return bulkClearSelectionMutation.mutateAsync({ selectedTaskIds });
  }, [bulkClearSelectionMutation]);

  return {
    addToProject,
    clearSelection,
    isAddingToProject: bulkAddToProjectMutation.isPending,
    isClearingSelection: bulkClearSelectionMutation.isPending,
    isLoading: bulkAddToProjectMutation.isPending || bulkClearSelectionMutation.isPending
  };
}; 